import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import CountUp from "react-countup";
import { useInView as useCountInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import NetworkCalculator from "./components/NetworkCalculator";
import { 
  MessageCircle, 
  X, 
  Send, 
  Menu, 
  CheckCircle2, 
  XCircle, 
  ArrowRight,
  Users,
  Briefcase,
  DollarSign,
  Clock,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
  Heart,
  Award,
  Loader2,
  ChevronDown,
  Play,
  Plus,
  Minus
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2211773d-f0b3-4564-83a5-4cc7f60a1377/artifacts/bzxne9u9_6744c91694f9840615c75764.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial scroll position
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "How It Works", href: "#how-it-works" },
    { label: "Earnings", href: "#earnings" },
    { label: "Testimonials", href: "#testimonials" }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass-dark py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a href="#" className="flex items-center">
            <img src={LOGO_URL} alt="Aspire Partners" className="h-10 md:h-12" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="nav-link text-sm">
                {link.label}
              </a>
            ))}
            <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
              <Button
                className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-6 py-2 font-semibold transition-all hover:scale-105"
                data-testid="nav-apply-btn"
              >
                Join Today
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="mobile-menu"
        >
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 text-white"
          >
            <X size={24} />
          </button>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="nav-link text-xl"
            >
              {link.label}
            </a>
          ))}
          <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>
            <Button
              className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-8 py-3 font-semibold mt-4"
            >
              Join Today
            </Button>
          </a>
        </motion.div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const benefits = [
    "No selling",
    "No startup cost",
    "No employees",
    "No quotas",
    "No MLM structure"
  ];

  return (
    <section className="hero-section bg-[#0B1120] relative" data-testid="hero-section">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute w-full h-full object-cover"
          style={{ minWidth: '100%', minHeight: '100%' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_intro-income/artifacts/jhnmy0gk_5870163_Family_Holiday_3840x2160.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="hero-overlay absolute inset-0" />

      {/* Animated Particles */}
      <div className="particles-bg">
        {[...Array(20)].map((_, i) => (
          <div
            key={`particle-${i}`}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 20}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      <motion.div 
        style={{ opacity }}
        className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-32"
      >
        <motion.p
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="eyebrow text-[#0099CC] mb-6"
        >
          Professional Referral Partnership
        </motion.p>

        <motion.h1
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-8 tracking-tight"
        >
          Turn Your Business Network
          <br />
          <span className="text-gradient">Into Residual Income</span>
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed"
        >
          You already know business owners, executives, and decision-makers.
          Now imagine getting paid every month for simply making the right introductions.
        </motion.p>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit}
              variants={scaleIn}
              className="flex items-center gap-2 px-4 py-2 rounded-full glass-dark"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span className="text-white text-sm font-medium">{benefit}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
            <Button
              className="cta-btn bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-blue-500/30 transition-all hover:scale-105"
              data-testid="hero-cta-btn"
            >
              Join Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="mt-4 text-[#0099CC] font-semibold text-lg">
            100% Risk FREE · Try it FREE for 30 days · Then just $97/month
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <a href="#problem" className="text-slate-400 hover:text-white transition-colors">
            <ChevronDown className="w-8 h-8 animate-bounce" />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Intro Video Section
const IntroVideoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-16 md:py-24 bg-white" ref={ref}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-10"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight">
            See How Aspire Partners Works
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.2 }}
          className="rounded-2xl overflow-hidden shadow-2xl"
        >
          <video
            controls
            className="w-full aspect-video"
            poster=""
          >
            <source src="https://customer-assets.emergentagent.com/job_intro-income/artifacts/4j6ej0jt_Aspire%20Intro.mov" type="video/quicktime" />
            <source src="https://customer-assets.emergentagent.com/job_intro-income/artifacts/4j6ej0jt_Aspire%20Intro.mov" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </motion.div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const networkTypes = [
    "Business owners",
    "CEOs & executives",
    "Decision makers",
    "Entrepreneurs",
    "Industry leaders"
  ];

  const solutions = [
    "Reduce operational costs",
    "Improve profitability",
    "Automate operations",
    "Improve employee benefits",
    "AI Marketing & Lead Generation"
  ];

  return (
    <section id="problem" className="py-24 md:py-32 bg-white relative" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="eyebrow text-[#0099CC] mb-4">The Problem</p>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mb-6 tracking-tight">
              Most Professionals Have a Powerful Network...
              <span className="block text-[#64748B] mt-2">But They Never Monetize It</span>
            </h2>
            <div className="section-divider !mx-0 !ml-0" />
            
            <p className="text-lg text-slate-600 mb-8">
              Over the course of your career you've built relationships with:
            </p>

            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="space-y-3 mb-8"
            >
              {networkTypes.map((type) => (
                <motion.div
                  key={type}
                  variants={fadeInUp}
                  className="flex items-center gap-3"
                >
                  <div className="w-2 h-2 rounded-full bg-[#0099CC]" />
                  <span className="text-slate-700 font-medium">{type}</span>
                </motion.div>
              ))}
            </motion.div>

            <p className="text-slate-600 mb-4">
              You introduce people all the time. <strong>Clients. Vendors. Opportunities.</strong>
            </p>
            <p className="text-slate-800 font-semibold text-lg">
              But here's the reality... Those introductions almost never generate income for you.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-br from-slate-50 to-white p-8 md:p-12 rounded-3xl border border-slate-100 shadow-xl"
          >
            <p className="text-slate-600 mb-6">
              Meanwhile, companies are actively searching for solutions that help them:
            </p>
            <div className="space-y-4 mb-8">
              {solutions.map((solution, index) => (
                <motion.div
                  key={solution}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100 card-hover"
                >
                  <div className="w-10 h-10 rounded-full bg-[#0099CC]/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#0099CC]" />
                  </div>
                  <span className="font-medium text-slate-700">{solution}</span>
                </motion.div>
              ))}
            </div>

            <div className="p-6 bg-[#0B1120] rounded-2xl">
              <p className="text-white font-semibold text-lg mb-2">
                Aspire Partners connects those companies to solutions that help them save money and operate more efficiently.
              </p>
              <p className="text-[#0099CC] font-bold">
                And when they implement those solutions... You earn recurring compensation.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    {
      number: "01",
      title: "Identify A Business",
      description: "Think of companies you already know. Business owners, executives, decision-makers. Companies often overpay for services like software, logistics, healthcare plans, or vendor contracts.",
      icon: <Users className="w-8 h-8" />
    },
    {
      number: "02",
      title: "Make A Simple Introduction",
      description: "You introduce them to Aspire Partners' trusted vendor partners. No pitching. No selling. Just a professional introduction.",
      icon: <Briefcase className="w-8 h-8" />
    },
    {
      number: "03",
      title: "Aspire Partners Handles Everything",
      description: "Our vendor partners handle: analysis, audits, presentations, implementation, and account management.",
      icon: <DollarSign className="w-8 h-8" />
    }
  ];

  return (
    <section id="how-it-works" className="py-24 md:py-32 bg-slate-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="eyebrow text-[#0099CC] mb-4">How It Works</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight">
            A Simple 3 Step Model
          </h2>
          <div className="section-divider" />
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.2 }}
              className="step-card bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#0099CC]/10 flex items-center justify-center text-[#0099CC]">
                  {step.icon}
                </div>
                <span className="font-numbers text-5xl font-bold text-slate-100">
                  {step.number}
                </span>
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-4">{step.title}</h3>
              <p className="text-slate-600 leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-12"
        >
          <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-8 py-4 text-lg font-semibold shadow-lg shadow-blue-500/30 transition-all hover:scale-105">
              Join Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="mt-4 text-[#0099CC] font-semibold">
            100% Risk FREE · Try it FREE for 30 days · Then just $97/month
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Why Professionals Love This Section
const WhyProfessionalsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const professionals = [
    "Executives",
    "Consultants",
    "Advisors",
    "Coaches",
    "Sales leaders",
    "Business owners"
  ];

  const reasons = [
    "Businesses constantly look for ways to reduce expenses",
    "Aspire Partners solutions help companies save money",
    "Vendor partners deliver the services",
    "You simply make the introduction"
  ];

  return (
    <section className="py-24 md:py-32 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="eyebrow text-[#0099CC] mb-4">Why Professionals Love This Model</p>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight mb-6">
              The Smartest Side Income Model for Professionals
            </h2>
            <div className="section-divider !mx-0 !ml-0" />

            <p className="text-lg text-slate-600 mb-6">
              Aspire Partners was designed for:
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {professionals.map((prof) => (
                <span
                  key={prof}
                  className="px-4 py-2 bg-slate-100 rounded-full text-slate-700 font-medium text-sm"
                >
                  {prof}
                </span>
              ))}
            </div>

            <p className="text-slate-700 font-medium">
              Professionals who already have strong networks and credibility.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-br from-[#0B1120] to-[#0F172A] p-8 md:p-12 rounded-3xl"
          >
            <h3 className="text-2xl font-bold text-white mb-8">Why it works</h3>
            <div className="space-y-4">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300 text-lg">{reason}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-[#0099CC] font-semibold text-lg">
                Which means no complicated sales process.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Earnings Section with Counter Animations
const EarningsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="earnings" className="py-24 md:py-32 bg-slate-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="eyebrow text-[#0099CC] mb-4">What You Can Earn</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight">
            One Introduction Can Create
            <span className="block text-[#0099CC]">Years of Income</span>
          </h2>
          <div className="section-divider" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mt-6">
            Unlike traditional consulting or sales roles... You don't get paid once.
            You earn residual income for the life of the client contract.
          </p>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-10"
        >
          <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-8 py-4 text-lg font-semibold shadow-lg shadow-blue-500/30 transition-all hover:scale-105">
              Join Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="mt-4 text-[#0099CC] font-semibold">
            100% Risk FREE · Try it FREE for 30 days · Then just $97/month
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Not MLM Section
const NotMLMSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const notItems = [
    "No recruiting required",
    "No product inventory",
    "No selling to friends or family",
    "No building or managing downlines",
    "No quotas",
    "Just make introductions"
  ];

  return (
    <section className="py-24 md:py-32 bg-white" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <p className="eyebrow text-[#0099CC] mb-4">Why This Is Different</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight mb-8">
            This Is <span className="text-red-500">NOT</span> An MLM
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-12"
        >
          {notItems.map((item) => {
            const isPositive = item === "Just make introductions";
            return (
              <motion.div
                key={item}
                variants={scaleIn}
                className={`flex items-center gap-3 p-4 rounded-xl border ${isPositive ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}
              >
                {isPositive ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                )}
                <span className="text-slate-700 font-medium text-left">{item}</span>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-[#0B1120] to-[#0F172A] p-8 md:p-12 rounded-3xl"
        >
          <Shield className="w-16 h-16 text-[#0099CC] mx-auto mb-6" />
          <h3 className="text-2xl font-bold text-white mb-4">
            Aspire Partners is a professional referral partnership model.
          </h3>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
            You introduce businesses to solutions that help them save money or improve operations.
            Our vendor partners handle everything else.
          </p>
          <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-8 py-4 text-lg font-semibold shadow-lg shadow-blue-500/30 transition-all hover:scale-105">
              Join Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="mt-4 text-[#0099CC] font-semibold">
            100% Risk FREE · Try it FREE for 30 days · Then just $97/month
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// What Companies Need Section
const CompaniesNeedSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const solutions = [
    { name: "Technology cost optimization", icon: <Sparkles className="w-6 h-6" /> },
    { name: "Healthcare cost reduction", icon: <Heart className="w-6 h-6" /> },
    { name: "Capital & lending solutions", icon: <DollarSign className="w-6 h-6" /> },
    { name: "Revenue recovery services", icon: <TrendingUp className="w-6 h-6" /> },
    { name: "Payment automation", icon: <Award className="w-6 h-6" /> },
    { name: "Legal guard services", icon: <Shield className="w-6 h-6" /> },
    { name: "Legal", icon: <Briefcase className="w-6 h-6" /> },
    { name: "Identity Protection", icon: <Users className="w-6 h-6" /> },
    { name: "AI Marketing & Lead Generation", icon: <Target className="w-6 h-6" /> }
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="eyebrow text-[#0099CC] mb-4">What Companies Need Today</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight">
            Businesses Are Searching For
            <span className="block text-[#0099CC]">Solutions Like This</span>
          </h2>
          <div className="section-divider" />
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mt-6">
            Companies are dealing with rising costs, operational inefficiencies, and employee retention challenges.
            Aspire vendor partners provide solutions like:
          </p>
        </motion.div>

        <div className="bento-grid max-w-4xl mx-auto">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-[#0099CC]/10 flex items-center justify-center text-[#0099CC]">
                {solution.icon}
              </div>
              <span className="font-semibold text-[#0F172A]">{solution.name}</span>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center text-lg font-semibold text-[#0099CC] mt-12"
        >
          These services help businesses reduce expenses and increase efficiency.
          <br />
          <span className="text-[#0F172A]">Which makes your introduction extremely valuable.</span>
        </motion.p>
      </div>
    </section>
  );
};

// Services Available Section
const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      id: "technology",
      title: "Aspire Technology Audit",
      description: "Aspire Technology Audit provides a free global technology audit powered by 800+ suppliers, helping organizations reduce costs, implement the right AI and technology solutions, and stay competitive — with all recommendations presented and managed for you.",
      icon: <Sparkles className="w-8 h-8" />,
      videoUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/cg04hbe4_aspire_technology_audit_v1.mp4",
      posterUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/mm29aznz_Screenshot%202026-03-25%20at%2011.15.29%E2%80%AFAM.png"
    },
    {
      id: "preventative-care",
      title: "Aspire Preventative Care",
      description: "Aspire Preventative Care helps employers combat rising healthcare costs by covering behavioral health, urgent care, and thousands of prescriptions through a non-insurance program that lowers out-of-pocket expenses. You book the meeting, we present and close — and you earn recurring monthly compensation per employee.",
      icon: <Heart className="w-8 h-8" />,
      videoUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/scqgzwm8_preventative_care_program_v1.mp4",
      posterUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/hf82us3x_Screenshot%202026-03-20%20at%208.20.50%E2%80%AFPM.png"
    },
    {
      id: "legal-guard",
      title: "Aspire Legal Guard",
      description: "Aspire Legal Guard is backed by a 50-year-old, $1B company serving 40,000+ businesses nationwide, delivering trusted legal and identity protection benefits. Professionals earn daily pay plus ongoing lifetime compensation through a proven, decades-long model.",
      icon: <Shield className="w-8 h-8" />,
      videoUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/h3kpp0k1_Legal_Guard.mp4",
      posterUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/e8y5uq5b_Screenshot%202026-03-20%20at%208.29.32%E2%80%AFPM.png"
    },
    {
      id: "healthcare-rcm",
      title: "Aspire Healthcare RCM",
      description: "Aspire Healthcare RCM integrates with your current EHR and billing systems to boost collections without disruption. With 98%+ clean claims and a 72-hour audit, it reduces denials and increases cash flow through a low-risk, performance-based model.",
      icon: <TrendingUp className="w-8 h-8" />,
      videoUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/7yigkf26_Aspire%20Healthcare%20RCM%20Feb%2023%202026.mov",
      posterUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/1pws9tw2_Screenshot%202026-03-20%20at%208.33.16%E2%80%AFPM.png"
    },
    {
      id: "lending-center",
      title: "Aspire Lending Center",
      description: "A one-platform capital solution offering SBA loans, lines of credit, equipment financing, working capital, and commercial real estate. You make the introduction while experts handle qualification and funding — delivering fast decisions and strong approvals for business owners.",
      icon: <DollarSign className="w-8 h-8" />,
      videoUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/8ma9rwrz_aspire_partners_lending_v1.mp4",
      posterUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/cbmsopo2_Screenshot%202026-03-25%20at%2011.19.39%E2%80%AFAM.png"
    },
    {
      id: "commercial-payment",
      title: "Aspire Commercial Payment",
      description: "Backed by a $4B virtual card leader, Aspire Commercial Payment turns AP and invoice automation into rebate-driven revenue. Companies reduce fraud and eliminate checks — while experts present and close for you, generating ongoing monthly residuals from every vendor payment.",
      icon: <Award className="w-8 h-8" />,
      videoUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/joixas7g_aspire_commercial_payments_v1.mp4",
      posterUrl: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/kqu6bkb7_Screenshot%202026-03-20%20at%208.18.50%E2%80%AFPM.png"
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="eyebrow text-[#0099CC] mb-4">Our Solutions</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight">
            Services Available Through
            <span className="block text-[#0099CC]">Aspire Partners</span>
          </h2>
          <div className="section-divider" />
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mt-6">
            Business professionals introduce companies to these services, while Aspire Partners and its vendor network manage audits, analysis, and implementation.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              className="bg-gradient-to-br from-slate-50 to-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group"
              data-testid={`service-card-${service.id}`}
            >
              {/* Video Placeholder */}
              <div className="aspect-video bg-slate-100 rounded-xl mb-6 overflow-hidden flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                {service.videoUrl ? (
                  <video
                    controls
                    className="w-full h-full object-cover"
                    poster={service.posterUrl || ""}
                  >
                    <source src={service.videoUrl} type={service.videoUrl.endsWith('.mov') ? 'video/quicktime' : 'video/mp4'} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="text-center p-4">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[#0099CC]/10 flex items-center justify-center text-[#0099CC]">
                      {service.icon}
                    </div>
                    <p className="text-sm text-slate-400">Video coming soon</p>
                  </div>
                )}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-[#0F172A] mb-3">{service.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Lifestyle Section (Video Background)
const LifestyleSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const benefits = [
    "More freedom",
    "More flexibility",
    "More time with family",
    "More travel",
    "More control over your life"
  ];

  return (
    <section ref={ref} className="lifestyle-section relative min-h-[70vh] overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute w-full h-full object-cover"
          style={{ minWidth: '100%', minHeight: '100%' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_intro-income/artifacts/lbaqp2s2_0_Family_Beach_3840x2160.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="lifestyle-overlay" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <p className="eyebrow text-[#0099CC] mb-4">Lifestyle & Freedom</p>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-6">
            Stop Trading Time For Money
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Most professionals are trapped in a model where income depends on hours worked.
            <br />
            <strong className="text-white">More hours = more income. Aspire Partners changes that.</strong>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20"
        >
          <p className="text-[#0099CC] font-bold text-xl mb-8">
            One introduction can generate income for years. That means:
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {benefits.map((benefit, index) => (
              <motion.span
                key={benefit}
                initial={{ opacity: 0, y: 10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="px-5 py-3 bg-white/10 rounded-full text-white font-medium"
              >
                {benefit}
              </motion.span>
            ))}
          </div>
          <p className="text-slate-300 mt-8 text-lg">
            Instead of building someone else's company...
            <br />
            <strong className="text-white">You're building your own residual income stream.</strong>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section with Partner Videos
const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const videos = [
    { 
      name: "Craig",
      url: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/zis0men5_Craig%20%28ready%29.mp4"
    },
    { 
      name: "Ed Byam",
      url: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/v8cs338s_Ed%20Byam%20%28ready%29.mp4"
    },
    { 
      name: "Janee",
      url: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/ch9746hn_Janee%20%28ready%29.mp4"
    },
    { 
      name: "Mark Wilson",
      url: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/3ll75orx_Mark%20Wilson%20%28ready%29.mp4"
    },
    { 
      name: "Patrick Dudley",
      url: "https://customer-assets.emergentagent.com/job_intro-income/artifacts/5qv5tmr3_Patrick%20Dudley%20%28ready%29.mp4"
    }
  ];

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="eyebrow text-[#0099CC] mb-4">Testimonials</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight mb-6">
            Professionals Across Multiple Industries Are Already Using the Aspire Partners Model
          </h2>
          <div className="section-divider" />
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mt-6">
            Consultants, executives, and business advisors are using Aspire Partners to help companies uncover operational savings while building long-term value through strategic introductions.
          </p>
          <p className="text-[#0099CC] font-semibold mt-4">
            Hear directly from partners who are already working within the system.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {videos.slice(0, 3).map((video, index) => (
            <motion.div
              key={video.name}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.2 }}
              className="video-card"
              data-testid={`testimonial-video-${index}`}
            >
              <div className="aspect-video rounded-2xl overflow-hidden bg-slate-100">
                <video
                  controls
                  className="w-full h-full object-cover"
                  preload="metadata"
                >
                  <source src={video.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Center the last 2 videos */}
        <div className="flex justify-center gap-8 mt-8">
          {videos.slice(3).map((video, index) => (
            <motion.div
              key={video.name}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: (index + 3) * 0.2 }}
              className="video-card w-full md:w-[calc(33.333%-1rem)] max-w-md"
              data-testid={`testimonial-video-${index + 3}`}
            >
              <div className="aspect-video rounded-2xl overflow-hidden bg-slate-100">
                <video
                  controls
                  className="w-full h-full object-cover"
                  preload="metadata"
                >
                  <source src={video.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Who This Is For Section
const WhoIsForSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const idealFor = [
    "Have strong business networks",
    "Know business owners or executives",
    "Want additional income streams",
    "Are tired of the corporate grind",
    "Want more flexibility and freedom",
    "Prefer relationship-based opportunities"
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-50" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <p className="eyebrow text-[#0099CC] mb-4">Who This Is For</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight mb-8">
            This Opportunity Is Ideal For Professionals Who:
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid sm:grid-cols-2 gap-4 mb-12"
        >
          {idealFor.map((item) => (
            <motion.div
              key={item}
              variants={scaleIn}
              className="flex items-center gap-3 p-5 bg-white rounded-xl border border-slate-100 shadow-sm"
            >
              <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
              <span className="text-slate-700 font-medium text-left">{item}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="text-xl font-semibold text-[#0F172A] mb-8"
        >
          If you know business leaders, owners or decision-makers...
          <span className="block text-[#0099CC] mt-2">You already have everything you need.</span>
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-8 py-4 text-lg font-semibold shadow-lg shadow-blue-500/30 transition-all hover:scale-105">
              Join Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="mt-4 text-[#0099CC] font-semibold">
            100% Risk FREE · Try it FREE for 30 days · Then just $97/month
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Support Section - Aspire Partners Pro
const SupportSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: <DollarSign className="w-7 h-7" />,
      title: "Higher Compensation",
      description: "Enhanced earnings across all services for committed Aspire Partners professionals."
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "Weekly Live Training & Expert Calls",
      description: "Ongoing access to leadership, vendors, and industry specialists."
    },
    {
      icon: <Briefcase className="w-7 h-7" />,
      title: "Scripts, Tools & Messaging Frameworks",
      description: "Professional outreach assets that protect credibility."
    },
    {
      icon: <Award className="w-7 h-7" />,
      title: "Full Training Center",
      description: "Clear, practical guidance on each service and when to use it."
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Priority Support",
      description: "Simple referral submission and ongoing assistance."
    },
    {
      icon: <Target className="w-7 h-7" />,
      title: "Leads",
      description: "Opportunity to have leads sent to you."
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-[#0B1120] to-[#0F172A] relative overflow-hidden" ref={ref}>
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#0099CC]/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0099CC]/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="eyebrow text-[#0099CC] mb-4">Professional Development</p>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-6">
            Introducing <span className="text-[#0099CC]">Aspire Partners Pro</span>
          </h2>
          <div className="section-divider" />
          <p className="text-lg text-slate-300 max-w-3xl mx-auto mt-6">
            Aspire Partners Pro is a professional development program that helps business professionals make confident, consistent introductions. It provides the tools, training, and support needed to succeed — without guesswork or trial and error.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#0099CC]/50 hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-[#0099CC]/20 flex items-center justify-center text-[#0099CC] mb-5">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-slate-400 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-slate-300 text-lg">
            Everything you need to <span className="text-[#0099CC] font-semibold">confidently make introductions</span> and build long-term residual income.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What exactly will I be doing?",
      answer: "You'll be making strategic introductions between companies and the Aspire Partners cost-reduction team. Our vendors handle the audits, analysis, proposals, contracts, and implementation."
    },
    {
      question: "Do I need expertise in cost reduction?",
      answer: "No. Aspire Partners provides the training, messaging frameworks, and resources needed to introduce companies confidently. The technical work is handled by the vendor specialists."
    },
    {
      question: "How long does it take to start?",
      answer: "Most partners begin making introductions within their first week. Because savings must be verified before contracts activate, results typically take several months depending on the service and company size."
    },
    {
      question: "Who fulfills the services?",
      answer: "Vetted vendors who specialize in operational cost reduction perform the audits, consultations, negotiations, and implementation. Partners never manage fulfillment. Partners role is simply to make the introduction."
    },
    {
      question: "What if I don't have a large network?",
      answer: "Many partners start with people already in their professional circle. Aspire Partners also provides outreach frameworks and a network of business professionals to help you begin conversations."
    },
    {
      question: "How do partners get compensated?",
      answer: "Partners receive a share of verified savings from any company they introduce that becomes a client. Many contracts run multiple years, creating long-term value from a single introduction."
    },
    {
      question: "Will this damage my professional relationships?",
      answer: "No. Partners act as trusted connectors introducing credible cost-reduction specialists. Companies only move forward if verified savings exist, so the introduction is value-driven rather than a sales pitch."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 md:py-32 bg-slate-50" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="eyebrow text-[#0099CC] mb-4">FAQ</p>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight">
            Frequently Asked Questions
          </h2>
          <div className="section-divider" />
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={`faq-${faq.question.substring(0, 20)}`}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-semibold text-[#0F172A] pr-4">{faq.question}</span>
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${openIndex === index ? 'bg-[#0099CC] text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {openIndex === index ? (
                    <Minus className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                </div>
              </button>
              <motion.div
                initial={false}
                animate={{
                  height: openIndex === index ? "auto" : 0,
                  opacity: openIndex === index ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-5 pt-0">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-12 p-8 bg-white rounded-2xl border border-slate-100 shadow-lg"
        >
          <h3 className="text-2xl font-bold text-[#0F172A] mb-4">
            Ready to Turn Your Network Into Income?
          </h3>
          <p className="text-slate-600 mb-6 max-w-xl mx-auto">
            Join professionals who are already earning recurring revenue from their business connections.
          </p>
          
          <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-full px-10 py-5 text-lg font-semibold shadow-lg shadow-blue-500/30 transition-all hover:scale-105">
              Join Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="mt-4 text-[#0099CC] font-semibold text-lg">
            100% Risk FREE · Try it FREE for 30 days · Then just $97/month
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Helper function to strip markdown formatting from text
const stripMarkdown = (text) => {
  return text
    .replace(/\*\*/g, '')  // Remove **bold** markers
    .replace(/\*/g, '')    // Remove *italic* markers
    .replace(/\_\_/g, '')  // Remove __bold__ markers
    .replace(/\_/g, '')    // Remove _italic_ markers
    .replace(/\#\#\#/g, '') // Remove ### headers
    .replace(/\#\#/g, '')  // Remove ## headers
    .replace(/\#/g, '');   // Remove # headers
};

// Helper function to convert URLs in text to clickable links
const renderMessageWithLinks = (text, isUserMessage = false) => {
  // First strip markdown formatting
  const cleanText = stripMarkdown(text);
  
  // Regex to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = cleanText.split(urlRegex);
  
  return parts.map((part, idx) => {
    if (part.match(urlRegex)) {
      // Clean up URL - remove trailing punctuation
      const cleanUrl = part.replace(/[.,;:!?)]+$/, '');
      const trailingChars = part.slice(cleanUrl.length);
      
      // Create a display-friendly label
      let displayText = cleanUrl;
      if (cleanUrl.includes('checkout.partnerwithaspirepartners.com')) {
        displayText = '🚀 Sign Up Now';
      } else if (cleanUrl.includes('workwithaspirepartners.com/info-call')) {
        displayText = '📞 Book a Call';
      } else if (cleanUrl.includes('workwithaspirepartners.com')) {
        displayText = '🔗 Learn More';
      }
      
      return (
        <span key={`link-${idx}-${cleanUrl.slice(-10)}`}>
          <a
            href={cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline font-medium hover:opacity-80 transition-opacity ${
              isUserMessage 
                ? 'text-white' 
                : 'text-[#0099CC] hover:text-[#007EA8]'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {displayText}
          </a>
          {trailingChars}
        </span>
      );
    }
    return <span key={`text-${idx}`}>{part}</span>;
  });
};

// Helper to generate unique message IDs
let messageIdCounter = 0;
const generateMessageId = () => `msg-${Date.now()}-${++messageIdCounter}`;

// AI Chat Widget
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      id: 'initial-welcome',
      role: "assistant", 
      content: "Hi — welcome to Aspire Partners.\n\nWe help professionals create income by making strategic introductions to businesses that may be overspending on operational expenses.\n\nYou don't have to sell anything or manage implementation — Aspire Partners handles that.\n\nAre you looking to:",
      showQuickReplies: true,
      quickReplies: ["Learn how it works", "See if I'm a fit", "Ask a question", "Get started"]
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const quickReplyOptions = [
    "How it works",
    "Am I a fit?",
    "Do I have to sell?",
    "What is Aspire Partners Pro?",
    "How much can I earn?",
    "Book a call",
    "Get started"
  ];

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []); // messagesEndRef is a ref, its .current changes but ref object is stable

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleQuickReply = (reply) => {
    if (reply === "Get started") {
      window.open('https://checkout.partnerwithaspirepartners.com/', '_blank');
      return;
    }
    if (reply === "Book a call") {
      window.open('https://workwithaspirepartners.com/info-call/', '_blank');
      return;
    }
    setInput(reply);
    // Simulate form submission
    const fakeEvent = { preventDefault: () => {} };
    sendMessageWithText(reply);
  };

  const sendMessageWithText = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = text.trim();
    setInput("");
    setMessages((prev) => [...prev, { id: generateMessageId(), role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { id: generateMessageId(), role: "assistant", content: response.data.response, showQuickReplies: true }
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { id: generateMessageId(), role: "assistant", content: "I apologize, but I'm having trouble connecting right now. Please try again or book a call with our team: https://workwithaspirepartners.com/info-call/" }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    sendMessageWithText(input);
  };

  return (
    <div className="chat-widget" data-testid="chat-widget">
      {/* Chat Window */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="chat-window"
          style={{ height: "520px" }}
        >
          {/* Header */}
          <div className="bg-[#0B1120] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0099CC] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Aspire Partners Assist</h4>
                <p className="text-xs text-slate-400">Here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="close-chat-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" style={{ maxHeight: "380px" }}>
            {messages.map((msg, index) => (
              <div key={msg.id}>
                <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-[#0099CC] text-white rounded-br-md"
                        : "bg-white text-slate-700 rounded-bl-md shadow-sm border border-slate-100"
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-line break-words overflow-hidden">
                      {renderMessageWithLinks(msg.content, msg.role === "user")}
                    </p>
                  </div>
                </div>
                {/* Quick Replies for assistant messages */}
                {msg.role === "assistant" && msg.showQuickReplies && index === messages.length - 1 && !isLoading && (
                  <div className="flex flex-wrap gap-2 mt-3 ml-1">
                    {(msg.quickReplies || quickReplyOptions).map((reply) => (
                      <button
                        key={reply}
                        onClick={() => handleQuickReply(reply)}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#0099CC] text-[#0099CC] hover:bg-[#0099CC] hover:text-white transition-colors"
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-slate-100">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 bg-white border-t border-slate-100">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 px-4 py-2 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0099CC]"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="w-10 h-10 rounded-full bg-[#0099CC] hover:bg-[#007EA8] text-white flex items-center justify-center transition-colors disabled:opacity-50"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Chat Bubble */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="chat-bubble"
        data-testid="chat-bubble-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </motion.button>
    </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#0B1120] py-12 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src={LOGO_URL} alt="Aspire Partners" className="h-10" />
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Aspire Partners. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-slate-400 hover:text-[#0099CC] text-sm transition-colors">
              Privacy Policy
            </a>
            <a href="https://aspirepartners.com/terms-conditions/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#0099CC] text-sm transition-colors">
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Navigation />
      <HeroSection />
      <NetworkCalculator />
      <IntroVideoSection />
      <ProblemSection />
      <HowItWorksSection />
      <WhyProfessionalsSection />
      <EarningsSection />
      <NotMLMSection />
      <CompaniesNeedSection />
      <ServicesSection />
      <LifestyleSection />
      <TestimonialsSection />
      <WhoIsForSection />
      <SupportSection />
      <FAQSection />
      <ChatWidget />
      <Footer />
    </div>
  );
}

export default App;
